import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, CheckCircle2, ExternalLink, Eye, EyeOff, Loader2, Pencil, Plus, Search, Star, Trash2, Youtube, AlertTriangle, RefreshCw, Download } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { api, fileUrl, uploadFile } from '../../lib/api';

const EMPTY = { title: '', description: '', source_url: '', thumbnail_url: '', collection: 'steve', category: 'steves_messages', featured: false, published: true, published_on: '', needs_replacement: false, needs_replacement_reason: '' };

const HEALTH = {
  ok: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  not_embeddable: 'bg-amber-100 text-amber-800 border-amber-200',
  missing: 'bg-red-100 text-red-800 border-red-200',
  unknown: 'bg-slate-100 text-slate-600 border-slate-200',
};

// Admin: paste a YouTube link, set title / description / category / order,
// publish or unpublish. No code changes needed to add videos.
export default function AdminVideoLibrary() {
  const [data, setData] = useState({ items: [], categories: {}, collections: {}, needs_replacement: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('steve');
  const [editing, setEditing] = useState(null);        // item | 'new'
  const [form, setForm] = useState(EMPTY);
  const [lookup, setLookup] = useState(null);
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/videos').then((r) => setData(r.data || { items: [] })).catch(() => toast.error('Could not load videos')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    try { const r = await fn(); if (okMsg) toast.success(okMsg); load(); return r; } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); return null; }
  };

  const items = data.items || [];
  const list = tab === 'needs' ? items.filter((v) => v.needs_replacement_text) : tab === 'drafts' ? items.filter((v) => !v.published) : items.filter((v) => (v.collection || 'steve') === tab);
  const ordered = [...list].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  const move = (v, dir) => {
    const ids = ordered.map((x) => x.id);
    const i = ids.indexOf(v.id); const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => api.post('/admin/videos/reorder', { ids }), 'Order updated');
  };
  const togglePublish = (v) => act(() => api.put(`/videos/${v.id}`, { published: !v.published }), v.published ? 'Video unpublished (kept in Admin)' : 'Video published');
  const setFeatured = (v) => act(() => api.put(`/videos/${v.id}`, { featured: !v.featured }), v.featured ? 'No longer featured' : 'Featured at the top of its collection');
  const remove = () => act(async () => { await api.delete(`/videos/${deleting.id}`); setDeleting(null); }, 'Removed. A copy is kept in the media trash.');

  const runHealth = async () => {
    setChecking(true);
    const r = await act(() => api.get('/admin/videos/health'));
    setChecking(false);
    if (r) toast.info(`${r.data.items.length} slot(s) checked. ${r.data.needs_replacement.length} need a replacement.`);
  };
  const importChannel = async () => {
    setImporting(true);
    const r = await act(() => api.post('/admin/videos/import-channel'));
    setImporting(false);
    if (r) toast.success(`${r.data.added.length} new video(s) added as drafts (${r.data.skipped_existing} already in the library).`);
  };

  const openEdit = (v) => {
    setLookup(null);
    if (v === 'new') setForm(EMPTY);
    else setForm({ title: v.title || '', description: v.description || '', source_url: v.source_url || v.watch_url || v.embed_url || '', thumbnail_url: v.thumbnail_url || '', collection: v.collection || 'steve', category: v.category || 'other', featured: !!v.featured, published: v.published !== false, published_on: v.published_on || '', needs_replacement: !!v.needs_replacement, needs_replacement_reason: v.needs_replacement_reason || '' });
    setEditing(v);
  };
  const doLookup = async () => {
    if (!form.source_url.trim()) return;
    setLooking(true);
    try {
      const r = await api.post('/admin/videos/lookup', { url: form.source_url.trim() });
      setLookup(r.data);
      setForm((f) => ({ ...f, title: f.title || r.data.title || '', thumbnail_url: f.thumbnail_url || r.data.thumbnail_url || '',
        collection: editing === 'new' ? (r.data.suggested_collection || f.collection) : f.collection,
        category: editing === 'new' && f.category === 'steves_messages' ? (r.data.suggested_category || f.category) : f.category }));
      if (r.data.already_in_library) toast.warning(`Already in the library as "${r.data.already_in_library.title}".`);
      else toast.success(r.data.available === false ? 'YouTube says this video is not available.' : 'Video found on YouTube.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Lookup failed'); } finally { setLooking(false); }
  };
  const onThumb = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const r = await uploadFile(file, 'videos'); setForm((f) => ({ ...f, thumbnail_url: r.url })); toast.success('Thumbnail uploaded (durable storage)'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.title.trim()) { toast.error('Give the video a title.'); return; }
    if (!form.source_url.trim() && editing === 'new') { toast.error('Paste the YouTube link.'); return; }
    setSaving(true);
    try {
      if (editing === 'new') { await api.post('/videos', form); toast.success('Video added'); }
      else { await api.put(`/videos/${editing.id}`, form); toast.success('Video saved'); }
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const cats = data.categories || {};
  const cols = data.collections || {};
  const counts = { steve: items.filter((v) => (v.collection || 'steve') === 'steve').length, other: items.filter((v) => v.collection === 'other').length, drafts: items.filter((v) => !v.published).length, needs: (data.needs_replacement || []).length };

  return (
    <div className="space-y-5" data-testid="admin-video-library-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-[#1a2744]">Video Library</h1>
          <p className="text-sm text-slate-500 mt-1">Paste a YouTube link, give it a title and category, choose the order, publish. Steve Lesperance Videos and Other CKL Election Videos are kept separate on the public page.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={runHealth} disabled={checking} className="h-11 border-slate-300 font-bold" data-testid="admin-videos-health-btn">{checking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Check all links</Button>
          <Button variant="outline" onClick={importChannel} disabled={importing} className="h-11 border-slate-300 font-bold" data-testid="admin-videos-import-btn">{importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Import latest from Steve's channel</Button>
          <Button onClick={() => openEdit('new')} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="admin-videos-add-btn"><Plus className="h-4 w-4 mr-1.5" /> Add video</Button>
        </div>
      </div>

      {counts.needs > 0 && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="admin-videos-needs-replacement-banner"><CardContent className="p-4 flex flex-wrap items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-700" />
          <p className="text-sm text-amber-900 font-semibold flex-1">{counts.needs} slot{counts.needs === 1 ? '' : 's'} need a replacement link or file. They are hidden from the public page but kept here with their title, description and order.</p>
          <Button size="sm" variant="outline" onClick={() => setTab('needs')} className="border-amber-400 text-amber-900 font-bold" data-testid="admin-videos-needs-view">Show</Button>
        </CardContent></Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 flex-wrap h-auto" data-testid="admin-videos-tabs">
          <TabsTrigger value="steve" className="font-bold" data-testid="admin-videos-tab-steve">Steve Lesperance Videos ({counts.steve})</TabsTrigger>
          <TabsTrigger value="other" className="font-bold" data-testid="admin-videos-tab-other">Other CKL Election Videos ({counts.other})</TabsTrigger>
          <TabsTrigger value="drafts" className="font-bold" data-testid="admin-videos-tab-drafts">Unpublished ({counts.drafts})</TabsTrigger>
          <TabsTrigger value="needs" className="font-bold" data-testid="admin-videos-tab-needs">Needs replacement ({counts.needs})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading videos...</div>
      ) : ordered.length === 0 ? (
        <Card data-testid="admin-videos-empty"><CardContent className="p-8 text-center text-slate-500">{tab === 'needs' ? 'Every slot has a working link.' : 'No videos in this view yet. Use Add video and paste a YouTube link.'}</CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="admin-videos-list">
          {ordered.map((v, idx) => (
            <Card key={v.id} className={v.needs_replacement_text ? 'border-amber-300' : ''} data-testid={`admin-video-row-${v.id}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-44 shrink-0">
                  <div className="aspect-video rounded-lg overflow-hidden bg-slate-900 relative">
                    {v.thumbnail_url ? <img src={fileUrl(v.thumbnail_url)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <Youtube className="absolute inset-0 m-auto h-8 w-8 text-slate-500" />}
                  </div>
                  <div className="mt-2 flex items-center justify-center gap-1">
                    <Button size="icon" variant="outline" onClick={() => move(v, -1)} disabled={idx === 0} className="h-8 w-8" aria-label="Move up" data-testid={`admin-video-up-${v.id}`}><ArrowUp className="h-4 w-4" /></Button>
                    <span className="text-xs text-slate-500 font-bold w-8 text-center">#{v.order ?? '-'}</span>
                    <Button size="icon" variant="outline" onClick={() => move(v, 1)} disabled={idx === ordered.length - 1} className="h-8 w-8" aria-label="Move down" data-testid={`admin-video-down-${v.id}`}><ArrowDown className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {v.public_visible ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><Eye className="h-3 w-3 mr-1" /> Public</Badge> : <Badge variant="outline" className="bg-slate-100 text-slate-600"><EyeOff className="h-3 w-3 mr-1" /> Hidden</Badge>}
                    {!v.published && <Badge variant="outline" className="border-slate-300 text-slate-600">Unpublished</Badge>}
                    {v.featured && <Badge className="bg-[#C9A84C] text-[#1a2744] border-0"><Star className="h-3 w-3 mr-1" /> Featured</Badge>}
                    <Badge variant="outline" className="border-slate-300 text-slate-700">{cols[v.collection] || 'Steve Lesperance Videos'}</Badge>
                    <Badge variant="outline" className="border-slate-300 text-slate-600">{cats[v.category] || v.category || 'Other'}</Badge>
                    {v.health_status && <Badge className={HEALTH[v.health_status] || HEALTH.unknown} title={v.health_note || ''} data-testid={`admin-video-health-${v.id}`}>{v.health_status === 'ok' ? 'Link OK' : v.health_status === 'not_embeddable' ? 'Embed restricted' : v.health_status === 'missing' ? 'Link broken' : 'Not checked'}</Badge>}
                  </div>
                  <p className="font-serif font-bold text-[#1a2744] mt-2 leading-snug" data-testid={`admin-video-title-${v.id}`}>{v.title}</p>
                  {v.description && <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{v.description}</p>}
                  {v.needs_replacement_text && (
                    <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid={`admin-video-needs-${v.id}`}>
                      <p className="font-bold">Needs replacement</p>
                      <p>{v.needs_replacement_text}</p>
                      <p className="mt-1 text-xs">To restore it: open Edit and paste a working YouTube link (youtube.com/watch, youtu.be or a playlist link), or upload a thumbnail and give a Watch link. It goes public again automatically once the link works.</p>
                    </div>
                  )}
                  {v.repair_note && <p className="text-xs text-slate-500 mt-1.5">Repair log: {v.repair_note}</p>}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    {v.watch_url && <a href={v.watch_url} target="_blank" rel="noreferrer" className="underline inline-flex items-center" data-testid={`admin-video-watch-${v.id}`}>Open on YouTube <ExternalLink className="h-3 w-3 ml-1" /></a>}
                    {v.youtube_id && <span>id {v.youtube_id}</span>}
                    {v.published_on && <span>published {v.published_on}</span>}
                    {v.thumbnail_health && <span>thumbnail: {v.thumbnail_health}</span>}
                  </div>
                </div>
                <div className="flex sm:flex-col gap-2 shrink-0">
                  <div className="flex items-center gap-2"><Switch checked={!!v.published} onCheckedChange={() => togglePublish(v)} data-testid={`admin-video-publish-${v.id}`} /><span className="text-xs font-bold text-slate-600">{v.published ? 'Published' : 'Draft'}</span></div>
                  <Button size="sm" variant="outline" onClick={() => openEdit(v)} className="font-bold" data-testid={`admin-video-edit-${v.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => setFeatured(v)} className="font-bold" data-testid={`admin-video-feature-${v.id}`}><Star className="h-3.5 w-3.5 mr-1" /> {v.featured ? 'Unfeature' : 'Feature'}</Button>
                  <Button size="sm" variant="outline" onClick={() => setDeleting(v)} className="font-bold text-red-700 border-red-200 hover:bg-red-50" data-testid={`admin-video-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Remove</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-video-dialog">
          <DialogHeader><DialogTitle className="font-black text-[#1a2744]">{editing === 'new' ? 'Add a video' : 'Edit video'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="vl-url">YouTube link *</Label>
              <div className="flex gap-2 mt-1">
                <Input id="vl-url" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..." className="h-11" data-testid="admin-video-url" />
                <Button type="button" variant="outline" onClick={doLookup} disabled={looking || !form.source_url.trim()} className="h-11 font-bold shrink-0" data-testid="admin-video-lookup-btn">{looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />} Look up</Button>
              </div>
              {lookup && (
                <div className={`mt-2 rounded-lg border p-3 text-sm ${lookup.available === false ? 'border-red-200 bg-red-50 text-red-800' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`} data-testid="admin-video-lookup-result">
                  {lookup.available === false ? <p><AlertTriangle className="inline h-4 w-4 mr-1" /> {lookup.note}</p> : (
                    <p><CheckCircle2 className="inline h-4 w-4 mr-1" /> Found: <strong>{lookup.title}</strong>{lookup.author ? ` on ${lookup.author}` : ''}{lookup.is_steve_channel ? ' (Steve\'s channel)' : ''}{lookup.embeddable === false ? '. Embedding is restricted; it will show a thumbnail with a Watch link.' : ''}</p>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="vl-title">Title *</Label>
              <Input id="vl-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} className="h-11 mt-1" data-testid="admin-video-title" />
            </div>
            <div>
              <Label htmlFor="vl-desc">Description (optional)</Label>
              <Textarea id="vl-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} maxLength={2000} className="mt-1" data-testid="admin-video-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Collection</Label>
                <Select value={form.collection} onValueChange={(v) => setForm({ ...form, collection: v })}>
                  <SelectTrigger className="h-11 mt-1" data-testid="admin-video-collection"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(cols).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`admin-video-collection-${k}`}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category / topic</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="h-11 mt-1" data-testid="admin-video-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(cats).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`admin-video-category-${k}`}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vl-date">Recorded / published date (optional)</Label>
                <Input id="vl-date" type="date" value={form.published_on} onChange={(e) => setForm({ ...form, published_on: e.target.value })} className="h-11 mt-1" data-testid="admin-video-published-on" />
              </div>
              <div>
                <Label>Thumbnail (optional, stored durably)</Label>
                <div className="mt-1 flex items-center gap-2">
                  {form.thumbnail_url && <img src={fileUrl(form.thumbnail_url)} alt="" className="h-11 w-20 object-cover rounded border" />}
                  <label className="inline-flex items-center h-11 px-3 rounded-md border border-slate-300 text-sm font-bold cursor-pointer hover:bg-slate-50">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{form.thumbnail_url ? 'Replace' : 'Upload'}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onThumb(e.target.files?.[0])} data-testid="admin-video-thumb-input" />
                  </label>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="admin-video-form-published" /> Published</label>
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="admin-video-form-featured" /> Featured at the top</label>
              {editing !== 'new' && <label className="flex items-center gap-2 text-sm font-bold text-slate-700"><Switch checked={form.needs_replacement} onCheckedChange={(v) => setForm({ ...form, needs_replacement: v })} data-testid="admin-video-form-needs" /> Needs replacement (hide from public)</label>}
            </div>
            {form.needs_replacement && (
              <div>
                <Label htmlFor="vl-reason">What is needed</Label>
                <Input id="vl-reason" value={form.needs_replacement_reason} onChange={(e) => setForm({ ...form, needs_replacement_reason: e.target.value })} placeholder="e.g. Re-upload the original MP4 or paste the new YouTube link" className="h-11 mt-1" data-testid="admin-video-form-needs-reason" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)} className="h-11 font-bold" data-testid="admin-video-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="admin-video-save">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} {editing === 'new' ? 'Add video' : 'Save changes'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="admin-video-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this video from the library?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.title}" will be removed from the public page. A copy is kept in the media trash. If you only want to hide it, use the Published switch instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-video-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-800" data-testid="admin-video-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
